/**
 * Buy One Get One (BOGO) popup.
 *
 * Each <bogo-popup> element represents one offer and reads its own products
 * from data attributes (set by sections/bogo-offers.liquid):
 *   - data-bogo-trigger-ids: comma-separated variant ids that trigger the offer
 *   - data-bogo-free-id: the variant id given for free
 *
 * When a trigger variant is added to the cart, the popup invites the customer to
 * add the free product. Accepting adds it via the AJAX cart and re-renders the
 * cart drawer/notification, matching the theme's add-to-cart flow.
 *
 * Requires pubsub.js (subscribe / PUB_SUB_EVENTS) and global.js (fetchConfig).
 *
 * The free item is added as a normal line item; the matching "Buy X Get Y"
 * automatic discount in Shopify Admin zeroes its price at checkout.
 */
if (!customElements.get('bogo-popup')) {
  customElements.define(
    'bogo-popup',
    class BogoPopup extends HTMLElement {
      constructor() {
        super();

        this.triggerIds = (this.dataset.bogoTriggerIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);
        this.freeId = String(this.dataset.bogoFreeId || '');
        this.adding = false;
        this.activeElement = null;

        this.content = this.querySelector('.bogo-popup__content');
        this.errorEl = this.querySelector('[data-bogo-error]');

        this.querySelectorAll('[data-bogo-close]').forEach((el) =>
          el.addEventListener('click', () => this.close())
        );
        const addButton = this.querySelector('[data-bogo-add]');
        if (addButton) addButton.addEventListener('click', () => this.addFreeItem(addButton));
        this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
      }

      connectedCallback() {
        if (!this.triggerIds.length || !this.freeId) return;
        if (typeof subscribe !== 'function') return;

        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (this.adding) return; // ignore our own add
          if (!event || event.source === 'bogo') return;
          const addedId = String(event.productVariantId || '');
          if (!this.triggerIds.includes(addedId)) return;
          this.maybeOpen();
        });
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
      }

      // Offer the free item every time a qualifying product is added. open()
      // closes any other open BOGO popup first so two can't stack.
      maybeOpen() {
        this.open();
      }

      open() {
        document
          .querySelectorAll('bogo-popup:not([hidden])')
          .forEach((popup) => popup !== this && popup.close());

        this.activeElement = document.activeElement;
        this.hideError();
        this.removeAttribute('hidden');
        document.body.classList.add('overflow-hidden');
        if (this.content) this.content.focus();
      }

      close() {
        this.setAttribute('hidden', '');
        document.body.classList.remove('overflow-hidden');
        if (this.activeElement && typeof this.activeElement.focus === 'function') {
          this.activeElement.focus();
        }
      }

      addFreeItem(button) {
        const cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        const sections = cart ? cart.getSectionsToRender().map((section) => section.id) : [];

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData();
        formData.append('id', this.freeId);
        formData.append('quantity', 1);
        if (cart) {
          formData.append('sections', sections.join(','));
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        this.adding = true;
        if (button) {
          button.setAttribute('aria-disabled', 'true');
          button.classList.add('loading');
        }

        fetch(`${window.routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.showError(response.description || response.message);
              return;
            }
            this.close();
            if (cart) cart.renderContents(response);
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'bogo',
              productVariantId: this.freeId,
              cartData: response,
            });
          })
          .catch(() => this.showError('Something went wrong. Please try again.'))
          .finally(() => {
            this.adding = false;
            if (button) {
              button.removeAttribute('aria-disabled');
              button.classList.remove('loading');
            }
          });
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message || 'Something went wrong. Please try again.';
        this.errorEl.removeAttribute('hidden');
      }

      hideError() {
        if (this.errorEl) this.errorEl.setAttribute('hidden', '');
      }
    }
  );
}
